import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import {
  Bell,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  CheckCheck,
  Check,
  AlertTriangle,
  AlertCircle,
  Info,
  ArrowRight,
  FileText,
  User,
} from "lucide-react";

import api from "../../services/api_client";
import "./Alertes.css";

// ============================================================
// TYPES
// ============================================================

const TYPES = [
  "CONTRACT_EXPIRING",
  "CONTRACT_EXPIRED",
  "PROBATION_ENDING",
  "DRAFT_CONTRACT",
  "OTHER_RH_ALERT",
];

const TYPE_LABELS = {
  CONTRACT_EXPIRING: "Contrat bientôt expiré",
  CONTRACT_EXPIRED: "Contrat expiré",
  PROBATION_ENDING: "Fin de période d'essai",
  DRAFT_CONTRACT: "Contrat en brouillon",
  OTHER_RH_ALERT: "Autre alerte RH",
};

// ============================================================
// PRIORITES
// ============================================================

const PRIORITE_CONFIG = {
  CRITICAL: {
    label: "Critique",
    icon: AlertTriangle,
    cls: "critical",
  },

  WARNING: {
    label: "Avertissement",
    icon: AlertCircle,
    cls: "warning",
  },

  INFO: {
    label: "Info",
    icon: Info,
    cls: "info",
  },
};

// ============================================================
// FORMAT DATE
// ============================================================

function formatDateHeure(value) {
  if (!value) {
    return "-";
  }

  try {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return `${date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })} à ${date.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  } catch (error) {
    console.error("Erreur format date :", error);
    return "-";
  }
}

// ============================================================
// EXTRACTION SECURISEE DES NOTIFICATIONS
// ============================================================

function extractNotifications(responseData) {
  // ----------------------------------------------------------
  // API retourne directement un tableau
  // ----------------------------------------------------------

  if (Array.isArray(responseData)) {
    return responseData.filter(Boolean);
  }

  // ----------------------------------------------------------
  // Protection générale
  // ----------------------------------------------------------

  if (
    !responseData ||
    typeof responseData !== "object"
  ) {
    return [];
  }

  // ----------------------------------------------------------
  // Plusieurs formats possibles
  // ----------------------------------------------------------

  const candidates = [
    responseData.items,
    responseData.notifications,
    responseData.results,
    responseData.data,
    responseData.data?.items,
    responseData.data?.notifications,
    responseData.data?.results,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate.filter(Boolean);
    }
  }

  return [];
}

// ============================================================
// BADGE PRIORITE
// ============================================================

function PrioriteBadge({ priorite }) {
  const config =
    PRIORITE_CONFIG[priorite] ||
    PRIORITE_CONFIG.INFO;

  const Icon = config.icon;

  return (
    <span
      className={`alerte-priorite-badge alerte-${config.cls}`}
    >
      <Icon size={12} />
      {config.label}
    </span>
  );
}

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================

export default function EmployeeAlertes() {
  const navigate = useNavigate();

  // ==========================================================
  // ETATS
  // ==========================================================

  const [notifications, setNotifications] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [totalGlobal, setTotalGlobal] =
    useState(0);

  const [unreadCount, setUnreadCount] =
    useState(0);

  const [filterLue, setFilterLue] =
    useState("");

  const [filterPriorite, setFilterPriorite] =
    useState("");

  const [filterType, setFilterType] =
    useState("");

  const [page, setPage] =
    useState(1);

  const [totalPages, setTotalPages] =
    useState(1);

  const pageSize = 12;

  // ==========================================================
  // PROTECTION PRINCIPALE
  // ==========================================================

  const safeNotifications = useMemo(() => {
    if (!Array.isArray(notifications)) {
      return [];
    }

    return notifications.filter(Boolean);
  }, [notifications]);

  // ==========================================================
  // RECUPERATION DES NOTIFICATIONS
  // ==========================================================

  const fetchNotifications = useCallback(
    async () => {
      setLoading(true);
      setError("");

      try {
        const params = {
          page,
          page_size: pageSize,
        };

        if (filterLue !== "") {
          params.est_lue = filterLue;
        }

        if (filterPriorite !== "") {
          params.priorite = filterPriorite;
        }

        if (filterType !== "") {
          params.type = filterType;
        }

        console.log(
          "[EMPLOYEE ALERTES] GET /notifications/",
          params
        );

        const response =
          await api.get(
            "/notifications",
            {
              params,
            }
          );

        const responseData =
          response?.data ?? {};

        console.log(
          "[EMPLOYEE ALERTES] Réponse API :",
          responseData
        );

        // ======================================================
        // EXTRACTION
        // ======================================================

        const items =
          extractNotifications(
            responseData
          );

        // TOUJOURS un tableau
        setNotifications(items);

        // ======================================================
        // TOTAL
        // ======================================================

        let total =
          items.length;

        if (
          responseData &&
          typeof responseData ===
            "object" &&
          typeof responseData.total ===
            "number"
        ) {
          total =
            responseData.total;
        }

        setTotalGlobal(
          Math.max(0, total)
        );

        // ======================================================
        // NON LUES
        // ======================================================

        let unread;

        if (
          responseData &&
          typeof responseData ===
            "object" &&
          typeof responseData.unread_count ===
            "number"
        ) {
          unread =
            responseData.unread_count;
        } else {
          unread =
            items.filter(
              (notification) =>
                notification &&
                notification.est_lue ===
                  false
            ).length;
        }

        setUnreadCount(
          Math.max(0, unread)
        );

        // ======================================================
        // PAGINATION
        // ======================================================

        let calculatedPages =
          Math.ceil(
            total / pageSize
          );

        if (
          responseData &&
          typeof responseData ===
            "object" &&
          typeof responseData.total_pages ===
            "number"
        ) {
          calculatedPages =
            responseData.total_pages;
        }

        setTotalPages(
          Math.max(
            1,
            Number(calculatedPages) ||
              1
          )
        );
      } catch (err) {
        console.error(
          "[EMPLOYEE ALERTES] Erreur API :",
          err
        );

        const backendMessage =
          err?.response?.data
            ?.detail ||
          err?.response?.data
            ?.message ||
          err?.message;

        setError(
          backendMessage ||
            "Impossible de récupérer les alertes."
        );

        // Très important
        setNotifications([]);

        setTotalGlobal(0);

        setUnreadCount(0);

        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    },
    [
      page,
      filterLue,
      filterPriorite,
      filterType,
    ]
  );

  // ==========================================================
  // CHARGEMENT INITIAL
  // ==========================================================

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // ==========================================================
  // RESET PAGE
  // ==========================================================

  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [
    filterLue,
    filterPriorite,
    filterType,
  ]);

  // ==========================================================
  // RESET FILTRES
  // ==========================================================

  const handleReset = () => {
    setFilterLue("");
    setFilterPriorite("");
    setFilterType("");
    setPage(1);
  };

  const hasActiveFilters =
    filterLue !== "" ||
    filterPriorite !== "" ||
    filterType !== "";

  // ==========================================================
  // STATISTIQUES
  // ==========================================================

  const totalCritiques =
    safeNotifications.filter(
      (notification) =>
        notification?.priorite ===
        "CRITICAL"
    ).length;

  const totalWarning =
    safeNotifications.filter(
      (notification) =>
        notification?.priorite ===
        "WARNING"
    ).length;

  // ==========================================================
  // MARQUER UNE NOTIFICATION COMME LUE
  // ==========================================================

  const handleMarkRead = async (
    id
  ) => {
    if (!id) {
      return;
    }

    try {
      await api.post(
        `/notifications/${id}/read`
      );

      await fetchNotifications();
    } catch (err) {
      console.error(
        "Erreur marquage notification :",
        err
      );

      window.alert(
        "Erreur lors du marquage comme lue."
      );
    }
  };

  // ==========================================================
  // MARQUER TOUTES LES NOTIFICATIONS COMME LUES
  // ==========================================================

  const handleMarkAllRead =
    async () => {
      try {
        await api.post(
          "/notifications/mark-all-read"
        );

        await fetchNotifications();
      } catch (err) {
        console.error(
          "Erreur marquage global :",
          err
        );

        window.alert(
          "Erreur lors du marquage global."
        );
      }
    };

  // ==========================================================
  // VOIR L'OBJET
  // ==========================================================

  const handleVoirObjet =
    async (notification) => {
      if (!notification) {
        return;
      }

      // Marquer comme lue
      if (
        notification.est_lue ===
          false &&
        notification.id
      ) {
        try {
          await api.post(
            `/notifications/${notification.id}/read`
          );
        } catch (err) {
          console.warn(
            "Impossible de marquer automatiquement comme lue.",
            err
          );
        }
      }

      // CONTRAT
      if (
        notification.entite ===
          "CONTRAT" &&
        notification.entite_id
      ) {
        navigate(
          `/contrats/modifier/${notification.entite_id}`
        );

        return;
      }

      // EMPLOYE
      if (
        notification.entite ===
          "EMPLOYE" &&
        notification.entite_id
      ) {
        navigate(
          `/employes/modifier/${notification.entite_id}`
        );
      }
    };

  // ==========================================================
  // PAGINATION
  // ==========================================================

  const renderPageNumbers =
    () => {
      const pages = [];

      const safeTotalPages =
        Math.max(
          1,
          Number(totalPages) || 1
        );

      const safePage =
        Math.min(
          Math.max(
            1,
            Number(page) || 1
          ),
          safeTotalPages
        );

      if (safeTotalPages <= 5) {
        for (
          let i = 1;
          i <= safeTotalPages;
          i += 1
        ) {
          pages.push(i);
        }
      } else if (
        safePage <= 3
      ) {
        pages.push(
          1,
          2,
          3,
          4,
          "...",
          safeTotalPages
        );
      } else if (
        safePage >=
        safeTotalPages - 2
      ) {
        pages.push(
          1,
          "...",
          safeTotalPages - 3,
          safeTotalPages - 2,
          safeTotalPages - 1,
          safeTotalPages
        );
      } else {
        pages.push(
          1,
          "...",
          safePage - 1,
          safePage,
          safePage + 1,
          "...",
          safeTotalPages
        );
      }

      return pages.map(
        (item, index) => {
          if (item === "...") {
            return (
              <span
                key={`ellipsis-${index}`}
                className="pagination-ellipsis"
              >
                ...
              </span>
            );
          }

          return (
            <button
              type="button"
              key={`page-${item}`}
              onClick={() =>
                setPage(item)
              }
              className={`pagination-circle-btn ${
                page === item
                  ? "active"
                  : ""
              }`}
            >
              {item}
            </button>
          );
        }
      );
    };

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div className="app-container">
      <main className="main-content">
        <div className="content-padding">

          {/* ==================================================
              HEADER
          ================================================== */}

          <div className="page-header">
            <div>
              <h1>
                <Bell size={26} />
                Alertes
              </h1>

              <p>
                Consultez et gérez vos alertes RH.
              </p>
            </div>
          </div>

          {/* ==================================================
              STATISTIQUES
          ================================================== */}

          <div className="stats-grid alertes-stats">

            {/* TOTAL */}

            <div className="stat-card glass-card">
              <div className="stat-icon-wrapper purple">
                <Bell
                  size={22}
                  className="stat-icon"
                />
              </div>

              <div className="stat-details">
                <span className="stat-label">
                  Total alertes
                </span>

                <span className="stat-value">
                  {totalGlobal}
                </span>

                <span className="stat-subtitle">
                  Toutes priorités confondues
                </span>
              </div>
            </div>

            {/* NON LUES */}

            <div className="stat-card glass-card">
              <div className="stat-icon-wrapper blue">
                <Info
                  size={22}
                  className="stat-icon"
                />
              </div>

              <div className="stat-details">
                <span className="stat-label">
                  Non lues
                </span>

                <span className="stat-value">
                  {unreadCount}
                </span>

                <span className="stat-subtitle">
                  Nécessitent votre attention
                </span>
              </div>
            </div>

            {/* WARNING */}

            <div className="stat-card glass-card">
              <div className="stat-icon-wrapper orange">
                <AlertCircle
                  size={22}
                  className="stat-icon"
                />
              </div>

              <div className="stat-details">
                <span className="stat-label">
                  Avertissements
                </span>

                <span className="stat-value">
                  {totalWarning}
                </span>

                <span className="stat-subtitle">
                  Priorité WARNING
                </span>
              </div>
            </div>

            {/* CRITIQUES */}

            <div className="stat-card glass-card">
              <div className="stat-icon-wrapper red">
                <AlertTriangle
                  size={22}
                  className="stat-icon"
                />
              </div>

              <div className="stat-details">
                <span className="stat-label">
                  Critiques
                </span>

                <span className="stat-value">
                  {totalCritiques}
                </span>

                <span className="stat-subtitle">
                  Action requise rapidement
                </span>
              </div>
            </div>

          </div>

          {/* ==================================================
              TABLEAU
          ================================================== */}

          <div className="table-card glass-card">

            {/* FILTRES */}

            <div className="alertes-filters-bar">

              {/* STATUT */}

              <div className="alertes-filter-group">
                <span className="alertes-filter-label">
                  Statut
                </span>

                <select
                  value={filterLue}
                  onChange={(e) =>
                    setFilterLue(
                      e.target.value
                    )
                  }
                  className="alertes-filter-select"
                >
                  <option value="">
                    Toutes
                  </option>

                  <option value="false">
                    Non lues
                  </option>

                  <option value="true">
                    Lues
                  </option>
                </select>
              </div>

              {/* PRIORITE */}

              <div className="alertes-filter-group">
                <span className="alertes-filter-label">
                  Priorité
                </span>

                <select
                  value={
                    filterPriorite
                  }
                  onChange={(e) =>
                    setFilterPriorite(
                      e.target.value
                    )
                  }
                  className="alertes-filter-select"
                >
                  <option value="">
                    Toutes
                  </option>

                  <option value="CRITICAL">
                    Critique
                  </option>

                  <option value="WARNING">
                    Avertissement
                  </option>

                  <option value="INFO">
                    Info
                  </option>
                </select>
              </div>

              {/* TYPE */}

              <div className="alertes-filter-group">
                <span className="alertes-filter-label">
                  Type
                </span>

                <select
                  value={filterType}
                  onChange={(e) =>
                    setFilterType(
                      e.target.value
                    )
                  }
                  className="alertes-filter-select"
                >
                  <option value="">
                    Tous
                  </option>

                  {TYPES.map(
                    (type) => (
                      <option
                        key={type}
                        value={type}
                      >
                        {TYPE_LABELS[
                          type
                        ] || type}
                      </option>
                    )
                  )}
                </select>
              </div>

              {/* ACTIONS */}

              <div className="alertes-filter-actions">

                {hasActiveFilters && (
                  <button
                    type="button"
                    className="alertes-reset-btn"
                    onClick={
                      handleReset
                    }
                  >
                    <RotateCcw
                      size={14}
                    />
                    Réinitialiser
                  </button>
                )}

                {unreadCount > 0 && (
                  <button
                    type="button"
                    className="alertes-markall-btn"
                    onClick={
                      handleMarkAllRead
                    }
                  >
                    <CheckCheck
                      size={14}
                    />
                    Tout marquer comme lu
                  </button>
                )}

              </div>
            </div>

            {/* ==================================================
                CONTENU
            ================================================== */}

            {loading ? (
              <div className="table-message">
                Chargement...
              </div>
            ) : error ? (
              <div className="table-message error">
                {error}
              </div>
            ) : safeNotifications.length ===
              0 ? (
              <div className="table-message">
                Aucune alerte trouvée. 🎉
              </div>
            ) : (
              <div className="alertes-list">

                {safeNotifications.map(
                  (
                    notification,
                    index
                  ) => {
                    if (
                      !notification
                    ) {
                      return null;
                    }

                    const id =
                      notification.id ??
                      `notification-${index}`;

                    const isRead =
                      notification.est_lue ===
                      true;

                    return (
                      <div
                        key={id}
                        className={`alerte-card ${
                          isRead
                            ? "lue"
                            : "non-lue"
                        }`}
                      >

                        {/* GAUCHE */}

                        <div className="alerte-card-left">

                          <PrioriteBadge
                            priorite={
                              notification.priorite
                            }
                          />

                          <div className="alerte-card-content">

                            {/* TITRE */}

                            <div className="alerte-card-header-row">

                              <span className="alerte-card-titre">
                                {notification.titre ||
                                  "Alerte"}
                              </span>

                              {!isRead && (
                                <span
                                  className="alerte-nonlue-dot"
                                  title="Non lue"
                                />
                              )}

                            </div>

                            {/* MESSAGE */}

                            <p className="alerte-card-message">
                              {notification.message ||
                                "Aucun message disponible."}
                            </p>

                            {/* META */}

                            <div className="alerte-card-meta">

                              <span className="alerte-type-badge">
                                {TYPE_LABELS[
                                  notification.type
                                ] ||
                                  notification.type ||
                                  "Autre"}
                              </span>

                              {notification.entite && (
                                <span className="alerte-entite-tag">

                                  {notification.entite ===
                                  "CONTRAT" ? (
                                    <FileText
                                      size={11}
                                    />
                                  ) : (
                                    <User
                                      size={11}
                                    />
                                  )}

                                  {notification.entite}

                                  {notification.entite_id
                                    ? ` #${notification.entite_id}`
                                    : ""}

                                </span>
                              )}

                              <span className="alerte-date-tag">
                                {formatDateHeure(
                                  notification.date_creation ||
                                    notification.created_at
                                )}
                              </span>

                            </div>
                          </div>
                        </div>

                        {/* ACTIONS */}

                        <div className="alerte-card-actions">

                          <button
                            type="button"
                            className="alerte-action-btn primary"
                            onClick={() =>
                              handleVoirObjet(
                                notification
                              )
                            }
                          >
                            <ArrowRight
                              size={14}
                            />
                            Voir
                          </button>

                          {!isRead && (
                            <button
                              type="button"
                              className="alerte-action-btn secondary"
                              onClick={() =>
                                handleMarkRead(
                                  notification.id
                                )
                              }
                              disabled={
                                !notification.id
                              }
                            >
                              <Check
                                size={14}
                              />
                              Lu
                            </button>
                          )}

                        </div>

                      </div>
                    );
                  }
                )}

              </div>
            )}

            {/* ==================================================
                PAGINATION
            ================================================== */}

            {!loading &&
              !error &&
              safeNotifications.length >
                0 &&
              totalPages > 1 && (
                <div className="pagination-container">

                  <button
                    type="button"
                    className="pagination-arrow-btn"
                    onClick={() =>
                      setPage(
                        (current) =>
                          Math.max(
                            1,
                            current - 1
                          )
                      )
                    }
                    disabled={page <= 1}
                  >
                    <ChevronLeft
                      size={18}
                    />
                  </button>

                  <div className="pagination-numbers">
                    {renderPageNumbers()}
                  </div>

                  <button
                    type="button"
                    className="pagination-arrow-btn"
                    onClick={() =>
                      setPage(
                        (current) =>
                          Math.min(
                            totalPages,
                            current + 1
                          )
                      )
                    }
                    disabled={
                      page >= totalPages
                    }
                  >
                    <ChevronRight
                      size={18}
                    />
                  </button>

                </div>
              )}

          </div>
        </div>
      </main>
    </div>
  );
}